import {
    AllEvents,
    ConfigurationContext,
    ElectionDefinition,
    queries,
} from '@news-mono/web-common'
import { useQuery } from '@tanstack/react-query'
import React, { useContext, useMemo } from 'react'
import {
    ElectionDataFetchError,
    ElectionText,
    ElectionTitle,
    ElectionTitleButton,
} from '../components'
import { getElectionPartyData } from '../data'
import { TheRaceRow } from './components/TheRaceRow/TheRaceRow'
import { TheRaceRowPlaceholder } from './components/TheRaceRowPlaceholder/TheRaceRowPlaceholder'
import { TheRaceWinIndicator } from './components/TheRaceWinIndicator'
import {
    TheRaceWidgetChevron,
    TheRaceWidgetContainer,
    TheRaceWidgetGraphContainer,
    TheRaceWidgetGraphDataContainer,
} from './TheRaceWidget.styled'
import { ElectionPredictionDisclaimer } from '../components/ElectionPredictionDisclaimer'
import {
    ElectionConfig,
    TransformedData,
} from '@west-australian-newspapers/election-api-types'

export interface TheRaceWidgetProps {
    electionDefinition: ElectionDefinition
    partiesToShow?: number
    redirectTo?: string
    onEvent: (event: AllEvents) => void
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
    titleText?: string
    colorMode?: 'light' | 'dark'
}

export const TheRaceWidget = ({
    electionDefinition,
    // Ignored if racePartyIds exists
    partiesToShow = 5,
    redirectTo,
    headingLevel,
    titleText = 'The Race',
    colorMode,
}: TheRaceWidgetProps) => {
    //set up initial data if available
    const config = useContext(ConfigurationContext)
    const { data, isError, isLoading } = useQuery(
        queries['election-api'].definition({
            initialDefinition: electionDefinition,
            electionAPI: config.electionApi,
            caller: config.apiCallerHeader,
        }),
    )

    return (
        <TheRaceWidgetContainer>
            {redirectTo ? (
                <ElectionTitleButton to={redirectTo}>
                    <ElectionTitle as={headingLevel}>{titleText}</ElectionTitle>
                    <TheRaceWidgetChevron />
                </ElectionTitleButton>
            ) : (
                <ElectionTitle as={headingLevel}>{titleText}</ElectionTitle>
            )}
            <ElectionText>Seats won (+likely to win)</ElectionText>
            <TheRaceWidgetGraphContainer>
                {isLoading ? (
                    <LoadingData placeholderAmount={partiesToShow} />
                ) : isError ||
                  !data ||
                  !data.electionData.data ||
                  !data.electionData.config ? (
                    <ElectionDataFetchError />
                ) : (
                    <LoadedData
                        partiesToShow={partiesToShow}
                        electionData={data.electionData.data}
                        config={data.electionData.config}
                        colorMode={colorMode}
                    />
                )}
            </TheRaceWidgetGraphContainer>
            <ElectionPredictionDisclaimer paddingTop={20} />
        </TheRaceWidgetContainer>
    )
}

type LoadingDataProps = {
    placeholderAmount: number
}

const LoadingData = ({ placeholderAmount }: LoadingDataProps) => {
    return (
        <TheRaceWidgetGraphDataContainer>
            {Array(placeholderAmount)
                .fill(0)
                .map((party, index) => (
                    <TheRaceRowPlaceholder key={index} />
                ))}
        </TheRaceWidgetGraphDataContainer>
    )
}

interface LoadedDataProps {
    electionData: TransformedData
    config: ElectionConfig
    partiesToShow: number
    colorMode?: 'light' | 'dark'
}
const LoadedData = ({
    electionData,
    config,
    partiesToShow,
    colorMode,
}: LoadedDataProps) => {
    const partyData = useMemo(
        () =>
            getElectionPartyData({
                dataFeed: electionData,
                partiesToShow,
                config,
            }),
        [electionData, config, partiesToShow],
    )
    const totalBarLength = config.barTotalSeats
    const seatsToWin = config.seatsToWin
    const percentPerSeat = 100 / totalBarLength

    const shouldDisplaySwing =
        partyData?.parties.find(
            (party) => party.swingPct !== undefined && party.swingPct !== null,
        ) !== undefined

    return (
        <>
            <TheRaceWinIndicator
                seatsToWin={seatsToWin}
                percentPerSeat={percentPerSeat}
                offsetIndicator={shouldDisplaySwing}
                colorMode={colorMode}
            />
            <TheRaceWidgetGraphDataContainer>
                {/* Now implement each individual row! */}
                {partyData.parties.map((party) => (
                    <TheRaceRow
                        key={party.code}
                        party={party}
                        percentPerSeat={percentPerSeat}
                        seatsToWin={seatsToWin}
                        displaySwing={shouldDisplaySwing}
                        colorMode={colorMode}
                    />
                ))}
            </TheRaceWidgetGraphDataContainer>
        </>
    )
}
